// Port of TrailMap/RouteRegionSplitter.swift (TrailBivvyKit): splits a route into downloadable offline regions.
//
// Stages are grouped greedily in travel order while a group stays within `maximumLength`; a single stage longer than
// that is cut into equal parts. Routes without stage distances are cut into equal parts. The i-th sub-route returned
// by `split` is the geometry of the i-th region returned by `TrailMapProvider.downloadableRegions`, so the app can plan
// each region with `OfflinePackagePlanner.plan({ route: subRoutes[i], regionID: regions[i].id, … })`.

import type {
  EpochMs,
  GeoBounds,
  GeoPolyline,
  OfflinePackageLevel,
  OfflineRegion,
  RoutePoint,
  TrailRoute,
} from '../core/models';
import { offlineCorridorHalfWidth } from '../core/domain';
import { boundsExpanded, cumulativeDistances, interpolate, polylineBounds } from '../core/geo';
import { OfflinePackagePlanner } from '../offline/OfflinePackagePlanner';
import type { TileLayerDescriptor } from '../offline/TileLayerDescriptor';

interface Piece {
  start: number;
  end: number;
  stageIDs: string[];
  /** Absolute start distances of `stageIDs` (empty when the route has no stage distances). */
  stageStarts: number[];
}

/** Default maximum length of one region along the route, in metres. */
const maximumRegionLength = 40_000;

const zeroBounds: GeoBounds = { minLatitude: 0, minLongitude: 0, maxLatitude: 0, maxLongitude: 0 };

export const RouteRegionSplitter = {
  /** Default maximum length of one region along the route, in metres. */
  maximumRegionLength,

  /** Identifier of the region at `index`: "region:<routeID>:<index>:<level>". */
  regionID(routeID: string, index: number, level: OfflinePackageLevel): string {
    return `region:${routeID}:${index}:${level}`;
  },

  /**
   * Consecutive sub-routes of at most `maximumLength` metres (ids "<routeID>#<index>"; names "<route name> i/n" when
   * split). Each sub-route lists the stages it touches, with stage start distances relative to its own start when the
   * route provides them. Empty routes yield no sub-route.
   */
  split(route: TrailRoute, maximumLength: number = maximumRegionLength): TrailRoute[] {
    const points = route.polyline.points;
    if (points.length === 0) return [];
    const cumulative = cumulativeDistances(route.polyline);
    const pieces = RouteRegionSplitter.pieces(route, cumulative, maximumLength);
    return pieces.map((piece, index) => {
      const sub: TrailRoute = {
        id: `${route.id}#${index}`,
        name: pieces.length === 1 ? route.name : `${route.name} ${index + 1}/${pieces.length}`,
        trailID: route.trailID,
        stageIDs: piece.stageIDs,
        isReversed: route.isReversed,
        polyline: RouteRegionSplitter.slice(points, cumulative, piece.start, piece.end),
        stageStartDistances: piece.stageStarts.map((s) => Math.max(0, s - piece.start)),
      };
      if (sub.trailID === undefined) delete sub.trailID;
      return sub;
    });
  },

  /** Regions for `route` with size estimates from `OfflinePackagePlanner.estimatedBytes` (tiles of `layers` + data). */
  regions(route: TrailRoute, level: OfflinePackageLevel, layers: readonly TileLayerDescriptor[], now: EpochMs): OfflineRegion[] {
    return RouteRegionSplitter.split(route).map((subRoute, index) => {
      const bounds = boundsExpanded(polylineBounds(subRoute.polyline) ?? zeroBounds, offlineCorridorHalfWidth(level));
      const region: OfflineRegion = {
        id: RouteRegionSplitter.regionID(route.id, index, level),
        name: subRoute.name,
        trailID: route.trailID,
        stageIDs: subRoute.stageIDs,
        bounds,
        level,
        estimatedBytes: OfflinePackagePlanner.estimatedBytes(subRoute, level, layers),
        downloadedBytes: 0,
        status: 'notDownloaded',
        createdAt: now,
        completedTiles: 0,
        totalTiles: 0,
      };
      if (region.trailID === undefined) delete region.trailID;
      return region;
    });
  },

  // MARK: - Internals

  pieces(route: TrailRoute, cumulative: readonly number[], maximumLength: number): Piece[] {
    const total = cumulative[cumulative.length - 1];
    if (total === undefined) return [];
    const limit = Number.isFinite(maximumLength) && maximumLength > 0 ? maximumLength : maximumRegionLength;

    let spans: Piece[] = [];
    if (route.stageIDs.length > 0 && route.stageStartDistances.length === route.stageIDs.length) {
      route.stageIDs.forEach((stageID, i) => {
        const start = Math.min(total, Math.max(0, route.stageStartDistances[i]));
        const nextStart = i + 1 < route.stageIDs.length ? route.stageStartDistances[i + 1] : total;
        const end = Math.min(total, Math.max(start, nextStart));
        spans.push({ start, end, stageIDs: [stageID], stageStarts: [start] });
      });
    } else {
      spans = [{ start: 0, end: total, stageIDs: [...route.stageIDs], stageStarts: [] }];
    }

    const pieces: Piece[] = [];
    let current: Piece | undefined;
    for (const span of spans) {
      const length = span.end - span.start;
      if (length > limit) {
        if (current) pieces.push(current);
        current = undefined;
        const parts = Math.ceil(length / limit);
        const step = length / parts;
        for (let k = 0; k < parts; k++) {
          const start = span.start + step * k;
          const end = k === parts - 1 ? span.end : span.start + step * (k + 1);
          pieces.push({ start, end, stageIDs: [...span.stageIDs], stageStarts: span.stageStarts.length === 0 ? [] : [start] });
        }
        continue;
      }
      if (current && span.end - current.start <= limit) {
        current = {
          start: current.start,
          end: span.end,
          stageIDs: [...current.stageIDs, ...span.stageIDs],
          stageStarts: [...current.stageStarts, ...span.stageStarts],
        };
      } else {
        if (current) pieces.push(current);
        current = span;
      }
    }
    if (current) pieces.push(current);
    return pieces;
  },

  /** The part of the polyline between two distances, with interpolated end points. */
  slice(points: readonly RoutePoint[], cumulative: readonly number[], start: number, end: number): GeoPolyline {
    if (points.length === 0 || cumulative.length !== points.length) return { points: [...points] };
    const result: RoutePoint[] = [RouteRegionSplitter.pointAtDistance(start, points, cumulative)];
    for (let i = 0; i < points.length; i++) {
      if (cumulative[i] > start && cumulative[i] < end) result.push(points[i]);
    }
    if (end > start) result.push(RouteRegionSplitter.pointAtDistance(end, points, cumulative));
    return { points: result };
  },

  pointAtDistance(distance: number, points: readonly RoutePoint[], cumulative: readonly number[]): RoutePoint {
    const first = points[0];
    const last = points[points.length - 1];
    const total = cumulative[cumulative.length - 1];
    if (!first || !last || total === undefined || points.length <= 1) {
      return first ?? { coordinate: { latitude: 0, longitude: 0 } };
    }
    if (distance <= 0) return first;
    if (distance >= total) return last;
    let low = 0;
    let high = cumulative.length - 1;
    while (high - low > 1) {
      const mid = (low + high) >> 1;
      if (cumulative[mid] <= distance) low = mid;
      else high = mid;
    }
    const length = cumulative[high] - cumulative[low];
    const t = length > 0 ? (distance - cumulative[low]) / length : 0;
    const a = points[low];
    const b = points[high];
    let elevation: number | undefined;
    if (a.elevation !== undefined && b.elevation !== undefined) elevation = a.elevation + (b.elevation - a.elevation) * t;
    else elevation = t < 0.5 ? a.elevation : b.elevation;
    const coordinate = interpolate(a.coordinate, b.coordinate, t);
    return elevation === undefined ? { coordinate } : { coordinate, elevation };
  },
};
